import type { CardDto } from '@kanban/shared';
import { dueState, isWithinCurrentIsoWeek } from '@kanban/shared';

/**
 * Filterleiste (SPEC 9.3).
 *
 * Bewusst client-seitig: das ganze Board liegt bereits im Cache. Ein Roundtrip
 * pro Tastendruck waere auf dem Smartphone spuerbar langsamer und braechte
 * nichts — gefiltert wird ueber hoechstens ein paar hundert Karten.
 */

export type DueFilter = 'alle' | 'ueberfaellig' | 'heute' | 'woche' | 'ohne';
export type AssigneeFilter = string | 'alle' | 'ich' | 'niemand';
export type LabelMode = 'und' | 'oder';

export interface CardFilter {
  assignee: AssigneeFilter;
  labelIds: string[];
  labelMode: LabelMode;
  due: DueFilter;
  /** `null` = egal, sonst genau diese Prioritaet. */
  priority: number | null;
  query: string;
  showCompleted: boolean;
}

export const EMPTY_FILTER: CardFilter = {
  assignee: 'alle',
  labelIds: [],
  labelMode: 'oder',
  due: 'alle',
  priority: null,
  query: '',
  showCompleted: true,
};

export function isFilterActive(filter: CardFilter): boolean {
  return (
    filter.assignee !== 'alle' ||
    filter.labelIds.length > 0 ||
    filter.due !== 'alle' ||
    filter.priority !== null ||
    filter.query.trim() !== '' ||
    !filter.showCompleted
  );
}

function matchesAssignee(card: CardDto, filter: CardFilter, meId: string): boolean {
  switch (filter.assignee) {
    case 'alle':
      return true;
    case 'ich':
      return card.assignees.some((a) => a.id === meId);
    case 'niemand':
      return card.assignees.length === 0;
    default:
      return card.assignees.some((a) => a.id === filter.assignee);
  }
}

function matchesLabels(card: CardDto, filter: CardFilter): boolean {
  if (filter.labelIds.length === 0) return true;
  const own = new Set(card.labels.map((label) => label.id));
  return filter.labelMode === 'und'
    ? filter.labelIds.every((id) => own.has(id))
    : filter.labelIds.some((id) => own.has(id));
}

function matchesDue(card: CardDto, filter: CardFilter, now: Date): boolean {
  if (filter.due === 'alle') return true;
  if (filter.due === 'ohne') return card.dueAt === null;
  if (!card.dueAt) return false;

  const state = dueState(card.dueAt, card.completedAt, now);
  if (filter.due === 'ueberfaellig') return state === 'overdue';
  if (filter.due === 'heute') return state === 'today';
  // "Diese Woche" schliesst Ueberfaelliges dieser Woche mit ein — was seit
  // Montag liegen geblieben ist, gehoert in dieselbe Ansicht.
  return isWithinCurrentIsoWeek(new Date(card.dueAt), now);
}

function matchesQuery(card: CardDto, filter: CardFilter): boolean {
  const needle = filter.query.trim().toLowerCase();
  if (!needle) return true;
  return (
    card.title.toLowerCase().includes(needle) ||
    (card.description ?? '').toLowerCase().includes(needle)
  );
}

export function matchesFilter(
  card: CardDto,
  filter: CardFilter,
  meId: string,
  now: Date = new Date(),
): boolean {
  if (!filter.showCompleted && card.completedAt) return false;
  if (filter.priority !== null && card.priority !== filter.priority) return false;
  return (
    matchesAssignee(card, filter, meId) &&
    matchesLabels(card, filter) &&
    matchesDue(card, filter, now) &&
    matchesQuery(card, filter)
  );
}

/** Filter in URL-Parameter uebersetzen, damit er teilbar ist (SPEC 9.3). */
export function filterToParams(filter: CardFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.assignee !== 'alle') params.set('assignee', filter.assignee);
  if (filter.labelIds.length > 0) params.set('labels', filter.labelIds.join(','));
  if (filter.labelMode !== 'oder') params.set('labelMode', filter.labelMode);
  if (filter.due !== 'alle') params.set('due', filter.due);
  if (filter.priority !== null) params.set('priority', String(filter.priority));
  if (filter.query.trim()) params.set('q', filter.query.trim());
  if (!filter.showCompleted) params.set('done', '0');
  return params;
}

export function filterFromParams(params: URLSearchParams): CardFilter {
  const due = params.get('due');
  const labelMode = params.get('labelMode');
  // `Number(null)` ist 0 — ohne die Pruefung auf null stuende der Filter nach
  // jedem Reload auf Prioritaet "keine" und blendete alles Wichtige aus.
  const rawPriority = params.get('priority');
  const priority = rawPriority === null ? Number.NaN : Number(rawPriority);

  return {
    assignee: params.get('assignee') ?? 'alle',
    labelIds: (params.get('labels') ?? '').split(',').filter(Boolean),
    labelMode: labelMode === 'und' ? 'und' : 'oder',
    due:
      due === 'ueberfaellig' || due === 'heute' || due === 'woche' || due === 'ohne' ? due : 'alle',
    priority: Number.isInteger(priority) && priority >= 0 && priority <= 3 ? priority : null,
    query: params.get('q') ?? '',
    showCompleted: params.get('done') !== '0',
  };
}
